import React, { useEffect, useState } from "react";
import API from "../services/api";
import { downloadPdf } from "../utils/downloadPdf";

const WithdrawRequestsManagement = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [selectedRequest, setSelectedRequest] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  // Filter state matched to your select dropdown design
  const [statusFilter, setStatusFilter] = useState("PENDING");

  // =========================================
  // FETCH ALL WITHDRAW REQUESTS
  // =========================================
  const fetchRequests = async () => {
    try {
      setLoading(true);
      setError("");

      const params = new URLSearchParams();
      if (statusFilter) {
        params.append("status", statusFilter);
      }

      const res = await API.get(`/withdraw-requests?${params.toString()}`);

      if (res.data.success) {
        setRequests(res.data.requests);
      }
    } catch (err) {
      setError(err.response?.data?.message || "Failed to load withdrawal requests");
    } finally {
      setLoading(false);
    }
  };

  // =========================================
  // VIEW REQUEST SINGLE DETAIL (DEEP DIVE)
  // =========================================
  const handleViewDetails = async (requestId) => {
    try {
      setDetailLoading(true);
      const res = await API.get(`/withdraw-requests/${requestId}`);

      if (res.data.success) {
        setSelectedRequest(res.data.withdrawReq);
        setIsModalOpen(true);
      }
    } catch (err) {
      alert("Error fetching payout detailed parameters");
    } finally {
      setDetailLoading(false);
    }
  };

  // =========================================
  // APPROVE AND PROCESS WITHDRAWAL PAYOUT
  // =========================================
  const handleApprovePayout = async () => {
    const confirmApprove = window.confirm(
      `Are you sure you want to mark this payout of ₹${selectedRequest.amount} as PAID?\nEnsure the bank transfer transaction is executed.`
    );
    if (!confirmApprove) return;

    const remarks = prompt("Enter bank payment reference / UTR number (Optional):");

    try {
      setActionLoading(true);
      const res = await API.put(`/withdraw-requests/${selectedRequest._id}/approve`, {
        remarks: remarks || "Paid via Admin Portal Dashboard",
      });

      if (res.data.success) {
        alert("Withdrawal marked as PAID successfully!");
        setIsModalOpen(false);
        fetchRequests();
      }
    } catch (err) {
      alert(err.response?.data?.message || "Failed to approve payment request");
    } finally {
      setActionLoading(false);
    }
  };

  // =========================================
  // REJECT WITHDRAWAL REQUEST & TRACE REFUND
  // =========================================
  const handleRejectPayout = async () => {
    const remarks = prompt("Please enter a mandatory rejection reason (this will display to the driver):");
    if (!remarks) {
      alert("A rejection reason is required to process refunds.");
      return;
    }

    try {
      setActionLoading(true);
      const res = await API.put(`/withdraw-requests/${selectedRequest._id}/reject`, {
        remarks,
      });

      if (res.data.success) {
        alert("Withdrawal request rejected. Funds reverted to driver wallet.");
        setIsModalOpen(false);
        fetchRequests();
      }
    } catch (err) {
      alert(err.response?.data?.message || "Failed to execute cancellation refund");
    } finally {
      setActionLoading(false);
    }
  };

  useEffect(() => {
    fetchRequests();
  }, [statusFilter]);

  if (loading && requests.length === 0) {
    return <div style={styles.loading}>Loading Payout Ledger Balances...</div>;
  }

  return (
    <div style={styles.page}>
      {/* HEADER SEGMENT */}
      <div style={styles.header}>
        <div>
          <h2 style={styles.title}>Withdrawal Payouts</h2>
          <p style={styles.subtitle}>
            Review driver account requests, verify settlements, and distribute transfers
          </p>
        </div>

        <div style={styles.filterGroup}>
          <select
            style={styles.select}
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="">All Statuses</option>
            <option value="PENDING">Pending</option>
            <option value="PAID">Paid / Settled</option>
            <option value="REJECTED">Rejected</option>
          </select>

          <div style={styles.badgeCount}>{requests.length} Requests</div>
        </div>
      </div>

      {error && <div style={styles.error}>{error}</div>}

      {/* WITHDRAW GRID */}
      <div style={styles.grid}>
        {requests.map((r) => (
          <div key={r._id} style={styles.card}>
            <div style={styles.cardTop}>
              <span
                style={{
                  ...styles.statusBadge,
                  backgroundColor:
                    r.paymentMethod === "UPI" ? "#e0f2fe" : "#f1f5f9",
                  color: r.paymentMethod === "UPI" ? "#0369a1" : "#475569",
                }}
              >
                {r.paymentMethod}
              </span>

              <span
                style={{
                  ...styles.approvalBadge,
                  backgroundColor: getStatusColor(r.status).bg,
                  color: getStatusColor(r.status).text,
                }}
              >
                {r.status}
              </span>
            </div>

            <h3 style={styles.driverName}>{r.driver?.name || "Unknown Driver"}</h3>
            <p style={styles.contactInfo}>{r.driver?.phone || "No Contact Link"}</p>
            <p style={styles.contactInfo}>{r.driver?.email || "No Contact Link"}</p>

            <div style={styles.vehicleBox}>
              <div style={styles.label}>Requested Amount</div>
              <div style={{ ...styles.vehicleText, fontSize: "20px", fontWeight: "800", color: "#1e293b", marginTop: "4px" }}>
                ₹{r.amount.toFixed(2)}
              </div>
            </div>

            <p style={{ fontSize: "11px", color: "#94a3b8", margin: "0 0 15px 0" }}>
              Submitted: {new Date(r.createdAt).toLocaleDateString()} at {new Date(r.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            </p>

            <button
              style={styles.viewBtn}
              onClick={() => handleViewDetails(r._id)}
            >
              {detailLoading && selectedRequest?._id === r._id
                ? "Accessing Data..."
                : "Review Payout Details"}
            </button>
          </div>
        ))}
      </div>

      {/* DETAILED MODAL OVERLAY */}
      {isModalOpen && selectedRequest && (
        <div style={styles.overlay} onClick={() => setIsModalOpen(false)}>
          <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <div>
                <h3 style={styles.modalTitle}>Payout Review: {selectedRequest.driver?.name}</h3>
                <div style={{ display: "flex", gap: "10px", marginTop: "5px" }}>
                  <span style={styles.modalSubtitle}>Request ID: {selectedRequest._id.slice(-8).toUpperCase()}</span>
                  <span
                    style={{
                      ...styles.approvalBadge,
                      fontSize: "10px",
                      backgroundColor: getStatusColor(selectedRequest.status).bg,
                      color: getStatusColor(selectedRequest.status).text,
                    }}
                  >
                    {selectedRequest.status}
                  </span>
                </div>
              </div>
              <button style={styles.closeBtn} onClick={() => setIsModalOpen(false)}>×</button>
            </div>

            <div style={styles.modalBody}>
              {/* CURRENT DRIVER METRICS BOX */}
              <div style={{ ...styles.statsRow, background: "#f8fafc", padding: "15px", borderRadius: "12px", marginBottom: "20px" }}>
                <div style={styles.statItem}>
                  <span style={styles.statLabel}>Current Wallet Balance</span>
                  <span style={{ ...styles.statValue, color: "#166534" }}>₹{selectedRequest.driver?.walletBalance || 0}</span>
                </div>
                <div style={styles.statItem}>
                  <span style={styles.statLabel}>Amount to Settle</span>
                  <span style={{ ...styles.statValue, color: "#b91c1c" }}>₹{selectedRequest.amount}</span>
                </div>
              </div>

              {/* SETTLEMENT BANK CHANNELS INFO */}
              <div style={styles.bankSection}>
                <h4 style={styles.sectionTitle}>Target Payout Settlement Route ({selectedRequest.paymentMethod})</h4>
                <div style={styles.bankGrid}>
                  {selectedRequest.paymentMethod === "UPI" ? (
                    <div style={{ gridColumn: "1 / -1" }}>
                      <p style={styles.docLabel}>UPI ID Address</p>
                      <p style={{ ...styles.bankValue, color: "#2563eb", fontSize: "16px", marginTop: "4px" }}>
                        {selectedRequest.driver?.upiId || "Missing Details"}
                      </p>
                    </div>
                  ) : (
                    <>
                      <div>
                        <p style={styles.docLabel}>Account Number</p>
                        <p style={styles.bankValue}>{selectedRequest.driver?.bankaccountnumber || "N/A"}</p>
                      </div>
                      <div>
                        <p style={styles.docLabel}>Bank Name Identifier</p>
                        <p style={styles.bankValue}>{selectedRequest.driver?.bankName || "N/A"}</p>
                      </div>
                      <div style={{ gridColumn: "1 / -1" }}>
                        <p style={styles.docLabel}>IFSC Code Address</p>
                        <p style={styles.bankValue}>{selectedRequest.driver?.ifscCode || "N/A"}</p>
                      </div>
                    </>
                  )}
                </div>
              </div>

              {selectedRequest.remarks && (
                <div style={{ marginTop: "15px", padding: "12px", background: "#f1f5f9", borderRadius: "8px" }}>
                  <p style={styles.docLabel}>ADMIN REMARKS / AUDIT LOG</p>
                  <p style={{ margin: "4px 0 0 0", fontSize: "13px", fontWeight: "600" }}>{selectedRequest.remarks}</p>
                </div>
              )}

              {/* ACTION FOOTER BUTTONS */}
              {selectedRequest.status === "PENDING" && (
                <div style={{ display: "flex", gap: "15px", marginTop: "30px" }}>
                  <button
                    disabled={actionLoading}
                    onClick={handleRejectPayout}
                    style={{
                      ...styles.viewBtn,
                      flex: 1,
                      backgroundColor: "#fee2e2",
                      color: "#991b1b",
                      border: "1px solid #fca5a5"
                    }}
                  >
                    Reject & Refund Wallet
                  </button>

                  <button
                    disabled={actionLoading}
                    onClick={handleApprovePayout}
                    style={{
                      ...styles.viewBtn,
                      flex: 1,
                      backgroundColor: "#dcfce7",
                      color: "#166534",
                      border: "1px solid #86efac",
                    }}
                  >
                    Confirm Paid
                  </button>
                </div>
              )}



{selectedRequest.status === "PAID" && (
  <div style={{ marginTop: "30px" }}>
    <button
      style={{
        ...styles.viewBtn,
        width: "100%",
        backgroundColor: "#eef2ff",
        color: "#4338ca",
        border: "1px solid #c7d2fe",
      }}
      onClick={() =>
        downloadPdf(
          `/documents/payout-receipt/${selectedRequest._id}`,
          `payout-${selectedRequest._id}.pdf`
        )
      }
    >
      Download Payout Receipt
    </button>
  </div>
)}



            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// =========================================
// STATE DISPLAY COLOUR MAPPING HELPERS
// =========================================
const getStatusColor = (status) => {
  switch (status) {
    case "PAID":
    case "APPROVED":
      return { bg: "#dcfce7", text: "#166534" };
    case "REJECTED":
      return { bg: "#fee2e2", text: "#991b1b" };
    case "PENDING":
    default:
      return { bg: "#fef3c7", text: "#92400e" };
  }
};

// =========================================
// PARENT SYNCED STYLES DESIGN SPECS
// =========================================
const styles = {
  page: { padding: "30px", background: "#f8fafc", minHeight: "100vh", fontFamily: "'Inter', sans-serif" },
  header: { display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginBottom: "30px", flexWrap: "wrap", gap: "20px" },
  title: { margin: 0, fontSize: "28px", color: "#1e293b", fontWeight: "700" },
  subtitle: { margin: "5px 0 0 0", color: "#64748b", fontSize: "14px" },
  filterGroup: { display: "flex", gap: "12px", alignItems: "center" },
  select: { padding: "10px 15px", borderRadius: "8px", border: "1px solid #e2e8f0", outline: "none", fontSize: "14px", background: "#fff" },
  badgeCount: { background: "#1e293b", color: "#fff", padding: "10px 18px", borderRadius: "8px", fontWeight: "600", fontSize: "14px" },
  grid: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: "20px" },
  card: { background: "#fff", borderRadius: "16px", padding: "20px", boxShadow: "0 4px 6px -1px rgba(0,0,0,0.1)" },
  cardTop: { display: "flex", justifyContent: "space-between", marginBottom: "15px" },
  statusBadge: { padding: "4px 10px", borderRadius: "6px", fontSize: "11px", fontWeight: "800" },
  approvalBadge: { padding: "4px 10px", borderRadius: "6px", fontSize: "11px", fontWeight: "800" },
  driverName: { margin: "0 0 4px 0", fontSize: "18px" },
  contactInfo: { marginBottom: "15px", color: "#64748b" },
  vehicleBox: { background: "#f8fafc", padding: "12px", borderRadius: "10px", marginBottom: "15px" },
  label: { fontSize: "11px", color: "#94a3b8", fontWeight: "700" },
  vehicleText: { fontSize: "14px" },
  statsRow: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px", marginBottom: "20px" },
  statItem: { display: "flex", flexDirection: "column" },
  statLabel: { fontSize: "11px", color: "#94a3b8" },
  statValue: { fontWeight: "700", fontSize: "16px" },
  viewBtn: { width: "100%", padding: "12px", borderRadius: "8px", border: "1px solid #e2e8f0", background: "#f8fafc", cursor: "pointer", fontWeight: "600" },
  overlay: { position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(15, 23, 42, 0.7)", display: "flex", justifyContent: "center", alignInterms: "center", zIndex: 1000, display: "flex", alignItems: "center" },
  modal: { background: "#fff", width: "95%", maxWidth: "550px", borderRadius: "24px", padding: "30px", maxHeight: "90vh", overflowY: "auto", margin: "0 auto" },
  modalHeader: { display: "flex", justifyContent: "space-between", marginBottom: "20px" },
  modalTitle: { margin: 0, fontSize: "22px" },
  modalSubtitle: { fontSize: "12px", color: "#94a3b8" },
  closeBtn: { border: "none", width: "32px", height: "32px", borderRadius: "50%", cursor: "pointer" },
  sectionTitle: { fontSize: "13px", color: "#64748b", fontWeight: "700", marginBottom: "15px" },
  bankSection: { background: "#f8fafc", padding: "20px", borderRadius: "16px" },
  bankGrid: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: "20px" },
  bankValue: { fontSize: "14px", fontWeight: "600", margin: "4px 0 0 0" },
  docLabel: { fontSize: "11px", color: "#94a3b8", fontWeight: "700", margin: 0 },
  loading: { textAlign: "center", padding: "100px", fontSize: "20px" },
  error: { background: "#fef2f2", color: "#b91c1c", padding: "15px", borderRadius: "8px", marginBottom: "20px" }
};

export default WithdrawRequestsManagement;